import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  format,
  isSameMonth,
  isSameDay,
  isBefore,
  isAfter,
  parseISO,
} from "date-fns";

// @mui material components
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKInput from "components/MKInput";
import MKAlert from "components/MKAlert";

// API
import { fetchAvailability, adminGetBlockedDates, adminBlockDates } from "api";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isDateBlocked(date, blockedRanges) {
  const dateStr = format(date, "yyyy-MM-dd");
  return blockedRanges.find((range) => dateStr >= range.start && dateStr < range.end);
}

function AdminMonthCalendar({ month, blockedRanges, selection, onDateClick }) {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const calStart = startOfWeek(monthStart);
  const calEnd = endOfWeek(monthEnd);

  const rows = [];
  let day = calStart;

  while (day <= calEnd) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = day;
      const inMonth = isSameMonth(currentDay, month);
      const blockInfo = isDateBlocked(currentDay, blockedRanges);
      const isAirbnb = blockInfo?.source === "airbnb";
      const isManual = blockInfo?.source === "manual";
      const isSelected =
        selection.start &&
        (isSameDay(currentDay, selection.start) ||
          (selection.end &&
            isAfter(currentDay, selection.start) &&
            (isBefore(currentDay, selection.end) || isSameDay(currentDay, selection.end))));

      let bgColor = "transparent";
      let textColor = "dark";

      if (!inMonth) {
        textColor = "light";
      } else if (isSelected) {
        bgColor = "#e3f2fd";
      }

      if (inMonth && isAirbnb) {
        bgColor = "#fff3e0";
      } else if (inMonth && isManual) {
        bgColor = "#fce4ec";
      }

      week.push(
        <MKBox
          key={currentDay.toISOString()}
          onClick={() => inMonth && onDateClick(currentDay)}
          textAlign="center"
          py={1}
          sx={{
            cursor: !inMonth ? "default" : "pointer",
            borderRadius: "8px",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            backgroundColor: bgColor,
            border: isSelected ? "2px solid" : "none",
            borderColor: isSelected ? "info.main" : "transparent",
            "&:hover": !inMonth ? {} : { backgroundColor: isSelected ? "#bbdefb" : "#e0e0e0" },
            transition: "all 0.15s",
          }}
        >
          <MKTypography
            variant="button"
            fontWeight="regular"
            color={textColor}
            sx={textColor === "light" ? { color: "#ccc" } : {}}
          >
            {inMonth ? format(currentDay, "d") : ""}
          </MKTypography>
        </MKBox>
      );
      day = addDays(day, 1);
    }
    rows.push(
      <Grid container key={day.toISOString()} spacing={0}>
        {week.map((cell, i) => (
          <Grid item xs key={i} sx={{ minWidth: 0 }}>
            {cell}
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <MKBox>
      <MKTypography variant="h6" textAlign="center" mb={2}>
        {format(month, "MMMM yyyy")}
      </MKTypography>
      <Grid container spacing={0} mb={1}>
        {WEEKDAYS.map((d) => (
          <Grid item xs key={d} sx={{ minWidth: 0 }}>
            <MKTypography variant="caption" textAlign="center" display="block" fontWeight="bold">
              {d}
            </MKTypography>
          </Grid>
        ))}
      </Grid>
      {rows}
    </MKBox>
  );
}

function AdminCalendar({ token, onAuthError }) {
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));
  const [allBlocked, setAllBlocked] = useState([]);
  const [manualBlocked, setManualBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [selection, setSelection] = useState({ start: null, end: null });
  const [blockDialog, setBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [editingBlocks, setEditingBlocks] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const start = format(currentMonth, "yyyy-MM-dd");
      const end = format(addMonths(currentMonth, 6), "yyyy-MM-dd");
      const [availData, manualData] = await Promise.all([
        fetchAvailability(start, end).catch(() => ({ blockedDates: [] })),
        adminGetBlockedDates(token).catch((err) => {
          if (err.message?.includes("Unauthorized")) onAuthError();
          return { blockedDates: [] };
        }),
      ]);
      setAllBlocked(availData.blockedDates || []);
      setManualBlocked(manualData.blockedDates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, token, onAuthError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDateClick = useCallback(
    (date) => {
      if (!selection.start || (selection.start && selection.end)) {
        setSelection({ start: date, end: null });
      } else {
        if (isBefore(date, selection.start) || isSameDay(date, selection.start)) {
          setSelection({ start: date, end: null });
        } else {
          setSelection({ start: selection.start, end: date });
        }
      }
    },
    [selection]
  );

  const handleBlock = useCallback(async () => {
    if (!selection.start || !selection.end) return;
    setActionLoading(true);
    try {
      await adminBlockDates(token, {
        action: "block",
        start: format(selection.start, "yyyy-MM-dd"),
        end: format(addDays(selection.end, 1), "yyyy-MM-dd"),
        reason: blockReason || "Manually blocked",
      });
      setSuccessMsg("Dates blocked successfully");
      setSelection({ start: null, end: null });
      setBlockDialog(false);
      setBlockReason("");
      await loadData();
    } catch (err) {
      if (err.message?.includes("Unauthorized")) {
        onAuthError();
        return;
      }
      setError(err.message || "Failed to block dates");
    } finally {
      setActionLoading(false);
    }
  }, [selection, blockReason, token, loadData, onAuthError]);

  const handleUnblock = useCallback(
    async (block) => {
      setActionLoading(true);
      try {
        await adminBlockDates(token, {
          action: "unblock",
          start: block.start,
          end: block.end,
        });
        setSuccessMsg("Dates unblocked successfully");
        await loadData();
      } catch (err) {
        if (err.message?.includes("Unauthorized")) {
          onAuthError();
          return;
        }
        setError(err.message || "Failed to unblock dates");
      } finally {
        setActionLoading(false);
      }
    },
    [token, loadData, onAuthError]
  );

  const startEditBlock = (i, block) => {
    setEditingBlocks((prev) => ({
      ...prev,
      [i]: {
        start: block.start,
        end: format(addDays(parseISO(block.end), -1), "yyyy-MM-dd"),
        reason: block.reason || "",
        original: block,
      },
    }));
  };

  const cancelEditBlock = (i) => {
    setEditingBlocks((prev) => {
      const next = { ...prev };
      delete next[i];
      return next;
    });
  };

  const saveEditBlock = useCallback(
    async (i) => {
      const edit = editingBlocks[i];
      if (!edit) return;
      setActionLoading(true);
      try {
        await adminBlockDates(token, {
          action: "unblock",
          start: edit.original.start,
          end: edit.original.end,
        });
        await adminBlockDates(token, {
          action: "block",
          start: edit.start,
          end: format(addDays(parseISO(edit.end), 1), "yyyy-MM-dd"),
          reason: edit.reason || "Manually blocked",
        });
        setSuccessMsg("Blocked dates updated");
        cancelEditBlock(i);
        await loadData();
      } catch (err) {
        if (err.message?.includes("Unauthorized")) {
          onAuthError();
          return;
        }
        setError(err.message || "Failed to update blocked dates");
      } finally {
        setActionLoading(false);
      }
    },
    [editingBlocks, token, loadData, onAuthError]
  );

  const nextMonth = addMonths(currentMonth, 1);

  return (
    <MKBox py={2}>
      {successMsg && (
        <MKAlert color="success" dismissible sx={{ mb: 2 }}>
          {successMsg}
        </MKAlert>
      )}
      {error && (
        <MKAlert color="error" dismissible sx={{ mb: 2 }}>
          {error}
        </MKAlert>
      )}

      <MKTypography variant="h5" mb={1}>
        <Icon sx={{ mr: 1, verticalAlign: "middle" }}>calendar_month</Icon>
        Calendar Management
      </MKTypography>
      <MKTypography variant="body2" fontWeight="bold" color="dark" mb={3}>
        Click to select a date range, then block it. Airbnb-synced blocks are shown in orange,
        manual blocks in pink.
      </MKTypography>

      {/* Selection actions */}
      {selection.start && (
        <MKBox
          display="flex"
          alignItems="center"
          gap={2}
          mb={2}
          p={2}
          bgColor="grey-100"
          borderRadius="lg"
        >
          <MKTypography variant="body2" fontWeight="bold">
            <strong>Selected:</strong> {format(selection.start, "MMM d, yyyy")}
            {selection.end ? ` → ${format(selection.end, "MMM d, yyyy")}` : " (select end date)"}
          </MKTypography>
          {selection.end && (
            <MKButton
              variant="gradient"
              color="error"
              size="small"
              onClick={() => setBlockDialog(true)}
              disabled={actionLoading}
            >
              <Icon sx={{ mr: 0.5 }}>block</Icon>
              Block Dates
            </MKButton>
          )}
          <MKButton
            variant="text"
            color="dark"
            size="small"
            onClick={() => setSelection({ start: null, end: null })}
          >
            Clear
          </MKButton>
        </MKBox>
      )}

      {/* Calendar navigation */}
      <MKBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
          <Icon>chevron_left</Icon>
        </IconButton>
        <MKButton variant="text" color="info" size="small" onClick={loadData}>
          <Icon sx={{ mr: 0.5 }}>refresh</Icon>
          Refresh
        </MKButton>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <Icon>chevron_right</Icon>
        </IconButton>
      </MKBox>

      {loading ? (
        <MKBox py={6} textAlign="center">
          <CircularProgress />
        </MKBox>
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <AdminMonthCalendar
              month={currentMonth}
              blockedRanges={allBlocked}
              selection={selection}
              onDateClick={handleDateClick}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AdminMonthCalendar
              month={nextMonth}
              blockedRanges={allBlocked}
              selection={selection}
              onDateClick={handleDateClick}
            />
          </Grid>
        </Grid>
      )}

      {/* Legend */}
      <MKBox display="flex" gap={3} mt={3} flexWrap="wrap">
        <MKBox display="flex" alignItems="center" gap={1}>
          <MKBox sx={{ width: 16, height: 16, bgcolor: "#fff", border: "1px solid #ccc" }} />
          <MKTypography variant="caption" fontWeight="bold">
            Available
          </MKTypography>
        </MKBox>
        <MKBox display="flex" alignItems="center" gap={1}>
          <MKBox sx={{ width: 16, height: 16, bgcolor: "#fff3e0", border: "1px solid #ccc" }} />
          <MKTypography variant="caption" fontWeight="bold">
            Airbnb (synced)
          </MKTypography>
        </MKBox>
        <MKBox display="flex" alignItems="center" gap={1}>
          <MKBox sx={{ width: 16, height: 16, bgcolor: "#fce4ec", border: "1px solid #ccc" }} />
          <MKTypography variant="caption" fontWeight="bold">
            Manually blocked
          </MKTypography>
        </MKBox>
        <MKBox display="flex" alignItems="center" gap={1}>
          <MKBox
            sx={{
              width: 16,
              height: 16,
              bgcolor: "#e3f2fd",
              border: "2px solid",
              borderColor: "info.main",
            }}
          />
          <MKTypography variant="caption" fontWeight="bold">
            Selected
          </MKTypography>
        </MKBox>
      </MKBox>

      {/* Manual blocked dates list */}
      {manualBlocked.length > 0 && (
        <MKBox mt={4}>
          <MKTypography variant="h6" mb={2}>
            Manually Blocked Dates
          </MKTypography>
          {manualBlocked.map((block, i) => {
            const editing = editingBlocks[i];
            if (editing) {
              return (
                <MKBox key={i} p={2} mb={1} bgColor="grey-100" borderRadius="lg">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <MKInput
                        type="date"
                        label="Start Date"
                        fullWidth
                        value={editing.start}
                        onChange={(e) =>
                          setEditingBlocks((p) => ({
                            ...p,
                            [i]: { ...p[i], start: e.target.value },
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <MKInput
                        type="date"
                        label="End Date"
                        fullWidth
                        value={editing.end}
                        onChange={(e) =>
                          setEditingBlocks((p) => ({
                            ...p,
                            [i]: { ...p[i], end: e.target.value },
                          }))
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <MKInput
                        label="Reason"
                        fullWidth
                        value={editing.reason}
                        onChange={(e) =>
                          setEditingBlocks((p) => ({
                            ...p,
                            [i]: { ...p[i], reason: e.target.value },
                          }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <MKBox display="flex" gap={1}>
                        <MKButton
                          variant="gradient"
                          color="info"
                          size="small"
                          onClick={() => saveEditBlock(i)}
                          disabled={actionLoading}
                        >
                          Save
                        </MKButton>
                        <MKButton
                          variant="text"
                          color="dark"
                          size="small"
                          onClick={() => cancelEditBlock(i)}
                        >
                          Cancel
                        </MKButton>
                      </MKBox>
                    </Grid>
                  </Grid>
                </MKBox>
              );
            }
            return (
              <MKBox
                key={i}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={1.5}
                mb={1}
                bgColor="grey-100"
                borderRadius="lg"
              >
                <MKTypography variant="body2" fontWeight="bold">
                  {block.start} → {format(addDays(parseISO(block.end), -1), "yyyy-MM-dd")}
                  {block.reason && ` — ${block.reason}`}
                </MKTypography>
                <MKBox display="flex" gap={1}>
                  <MKButton
                    variant="text"
                    color="info"
                    size="small"
                    onClick={() => startEditBlock(i, block)}
                  >
                    <Icon sx={{ mr: 0.5 }}>edit</Icon>
                    Edit
                  </MKButton>
                  <MKButton
                    variant="text"
                    color="error"
                    size="small"
                    onClick={() => handleUnblock(block)}
                    disabled={actionLoading}
                  >
                    <Icon>delete</Icon>
                    Remove
                  </MKButton>
                </MKBox>
              </MKBox>
            );
          })}
        </MKBox>
      )}

      {/* Block dialog */}
      <Dialog open={blockDialog} onClose={() => setBlockDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Block Dates</DialogTitle>
        <DialogContent>
          <MKTypography variant="body2" fontWeight="bold" mb={2}>
            Block {selection.start && format(selection.start, "MMM d, yyyy")} to{" "}
            {selection.end && format(selection.end, "MMM d, yyyy")}?
          </MKTypography>
          <MKInput
            label="Reason (optional)"
            fullWidth
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="e.g., Personal use, Maintenance"
          />
        </DialogContent>
        <DialogActions>
          <MKButton variant="text" color="dark" onClick={() => setBlockDialog(false)}>
            Cancel
          </MKButton>
          <MKButton variant="gradient" color="error" onClick={handleBlock} disabled={actionLoading}>
            {actionLoading ? "Blocking..." : "Block Dates"}
          </MKButton>
        </DialogActions>
      </Dialog>
    </MKBox>
  );
}

AdminMonthCalendar.propTypes = {
  month: PropTypes.instanceOf(Date).isRequired,
  blockedRanges: PropTypes.array.isRequired,
  selection: PropTypes.object.isRequired,
  onDateClick: PropTypes.func.isRequired,
};

AdminCalendar.propTypes = {
  token: PropTypes.string.isRequired,
  onAuthError: PropTypes.func.isRequired,
};

export default AdminCalendar;
